<template>
  <v-card class="w-100 h-100 pa-1">
    <div class="d-flex flex-column align-center h-100">
      <span style="font-size: 3vh">{{ title }}</span>

      <div class="d-flex w-100 h-100">
        <template v-if="loading">
          <div
            class="d-flex flex-column align-center justify-center h-100 w-100"
          >
            <v-progress-circular indeterminate></v-progress-circular>
            <span style="font-size: 2vh">Carregando dados...</span>
          </div>
        </template>
        <template v-if="!loading && chartLabels.length == 0">
          <div
            class="d-flex flex-column align-center justify-center h-100 w-100"
            style="font-size: 3vh"
          >
            <v-icon class="text-grey" icon="mdi-alert-circle"></v-icon>
            <span class="text-grey-darken-3 font-weight"
              >Dados Indisponíveis</span
            >
          </div>
        </template>
        <template v-if="!loading && chartLabels.length > 0">
          <div class="px-1 h-100" style="width: 80%">
            <canvas style="margin: 0px 0 0 -30px" :id="canvasId"></canvas>
          </div>

          <div
            class="d-flex flex-column align-center justify-center h-100"
            style="width: 20%"
          >
            <v-sheet
              height="10vh"
              width="14vh"
              border
              style="border-color: #424242"
              class="d-flex flex-column align-center justify-center"
            >
              <div class="d-flex align-center justify-space-evenly w-100">
                <span style="font-size: 5vh"
                  >{{ lastDifferencePercentage
                  }}<span style="font-size: 2vh">%</span></span
                >
                <v-avatar
                  :color="lastValueGood ? 'success' : 'error'"
                  style="height: 4.5vh; width: 4.5vh"
                  ><v-icon
                    size="4vh"
                    :icon="
                      lastDifferencePositive
                        ? 'mdi-arrow-top-right-thin'
                        : 'mdi-arrow-bottom-right-thin'
                    "
                  ></v-icon
                ></v-avatar>
              </div>
              <div
                class="d-flex align-center justify-space-evenly w-100 bg-grey-darken-3 flex-grow-1"
              >
                <span style="font-size: 1.5vh">Última Comparação</span>
              </div>
            </v-sheet>
          </div>
        </template>
      </div>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { Chart } from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { computed, nextTick, watch } from "vue";

const props = defineProps<{
  title: string;
  canvasId: string;
  chartLabels: string[];
  chartData: number[];
  limitValue: number;
  loading: boolean;
}>();

const limitData = computed(() => {
  return props.chartData.map((value) => {
    return props.limitValue;
  });
});

const lastDifferencePercentage = computed(() => {
  if (props.chartData.length < 2) return 0;
  const lastValue = props.chartData[props.chartData.length - 1];
  const previousValue = props.chartData[props.chartData.length - 2];
  const difference = lastValue - previousValue;
  const percentage = (difference / previousValue) * 100;
  return Math.abs(Math.round(percentage));
});

const lastDifferencePositive = computed(() => {
  if (props.chartData.length < 2) return false;
  const lastValue = props.chartData[props.chartData.length - 1];
  const previousValue = props.chartData[props.chartData.length - 2];
  return lastValue > previousValue;
});

const lastValueGood = computed(() => {
  if (props.chartData.length < 1) return false;
  const lastValue = props.chartData[props.chartData.length - 1];
  return lastValue <= props.limitValue;
});

let chart: Chart | null = null;

watch(
  () => props.chartData,
  () => {
    if (props.chartData.length === 0 || props.chartLabels.length === 0) return;

    initializeChart();
  },
  { once: true, deep: true }
);

async function initializeChart() {
  await nextTick();

  const canvas = document.getElementById(props.canvasId) as HTMLCanvasElement;
  if (!canvas) {
    console.warn("Canvas com ID 'graficoTempo' não encontrado.");
    return;
  }

  const ctx = canvas.getContext("2d");

  chart = new Chart(ctx!, {
    data: {
      labels: props.chartLabels,
      datasets: [
        {
          type: "line",
          label: `Padrão IAT/PR (${props.limitValue})`,
          data: limitData.value,
          borderColor: "#4CAF50",
          backgroundColor: "#4CAF50",
          pointRadius: 0,
          borderWidth: 3,
        },
        {
          type: "bar",
          label: props.title,
          data: props.chartData,
          borderColor: "#2196F3",
          backgroundColor: "#2196F3",
          borderWidth: 1,
        },
      ],
    },
    plugins: [ChartDataLabels],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 0,
          bottom: 10,
          left: 40,
          right: 0,
        },
      },
      plugins: {
        legend: {
          display: true,
          labels: {
            padding: 20,
            font: function (context) {
              const width = context.chart.width;
              const size = Math.round(width / 50);

              return {
                weight: "bold",
                size: size,
              };
            },
          },
        },
        title: {
          display: false,
        },
        datalabels: {
          display: function (context) {
            return context.datasetIndex == 1;
          },
          color: function (context) {
            const index = context.dataIndex;
            const value = context.dataset.data[index] as number;
            return value <= props.limitValue ? "#4CAF50" : "#F44336";
          },
          font: function (context) {
            const width = context.chart.width;
            const size = Math.round(width / 50);

            return {
              weight: "bold",
              size: size,
            };
          },
          align: "end",
          anchor: "end",
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: false,
          },
          ticks: {
            color: "black",
            font: function (context) {
              const width = context.chart.width;
              const size = Math.round(width / 60);

              return {
                weight: "bold",
                size: size,
              };
            },
          },
        },
        y: {
          display: true,
          min: 0,
          ticks: {
            padding: 10,
            color: "black",
            font: function (context) {
              const width = context.chart.width;
              const size = Math.round(width / 60);

              return {
                weight: "bold",
                size: size,
              };
            },
          },
        },
      },
    },
  });
}
</script>
